import {
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  IdcardOutlined,
  MoreOutlined,
  SearchOutlined,
  StopOutlined,
  SafetyOutlined,
  DownloadOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Dropdown,
  Empty,
  Image,
  Input,
  Modal,
  Skeleton,
  Space,
  Table,
  Tag,
  Watermark,
  notification,
  Tooltip,
  Badge,
  Checkbox,
  Popover,
  Typography,
  Statistic,
  Row,
  Col,
} from "antd";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import img3 from "../../images/3month.png";

const { Text } = Typography;

const CONN_KEY_SENAT = process.env.REACT_APP_NOT_SECRET_CODE_R;
const AUTH_KEY = "jygoTzpbzrNwh93HZCKMnPea6trQLUpd";
const API_GETG = "https://appmobile.svurguns.cyou/Data/api/GetG.php";
const API_VERIFY_PHONE = "https://appmobile.svurguns.cyou/Data/api/VerifyPhone.php";

// ─── Notification helper (AntD toast) ───────────────────────────────────────
const toast = {
  success: (msg) => notification.success({ message: msg, placement: "bottomRight", duration: 3 }),
  error: (msg) => notification.error({ message: msg, placement: "bottomRight", duration: 4 }),
  warning: (msg) => notification.warning({ message: msg, placement: "bottomRight", duration: 3 }),
};

// ─── Audit log helpers ───────────────────────────────────────────────────────
const AUDIT_KEY = "appgirls_audit_log";
const MAX_AUDIT = 200;

const addAuditLog = (action, userid, detail = "") => {
  try {
    const stored = JSON.parse(localStorage.getItem(AUDIT_KEY) || "[]");
    const entry = {
      id: Date.now(),
      time: new Date().toLocaleString("az-AZ"),
      action,
      userid: String(userid),
      detail,
    };
    const updated = [entry, ...stored].slice(0, MAX_AUDIT);
    localStorage.setItem(AUDIT_KEY, JSON.stringify(updated));
  } catch {}
};

const getAuditLog = () => {
  try {
    return JSON.parse(localStorage.getItem(AUDIT_KEY) || "[]");
  } catch {
    return [];
  }
};

function AppGirls() {
  // ═══════════════════════════════════════════════════════
  // STATES
  // ═══════════════════════════════════════════════════════
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [error, setError] = useState(null);

  // global search
  const [globalSearch, setGlobalSearch] = useState("");

  // column visibility
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("appgirls_hidden_cols") || "[]");
    } catch {
      return [];
    }
  });

  // table search
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  // selection (bulk actions)
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // modals
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [pin, setPin] = useState("");

  const [blockOpen, setBlockOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [staffOutReason, setStaffOutReason] = useState("");
  const [staffOutDate, setStaffOutDate] = useState("");

  const [timeOpen, setTimeOpen] = useState(false);
  const [cardId, setCardId] = useState("");

  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryTitle, setSummaryTitle] = useState("");
  const [summaryRows, setSummaryRows] = useState([]);

  const [auditOpen, setAuditOpen] = useState(false);

  const [bulkBlockOpen, setBulkBlockOpen] = useState(false);
  const [bulkBlockReason, setBulkBlockReason] = useState("");

  // phone verify loading per userid
  const [phoneVerifyLoading, setPhoneVerifyLoading] = useState({});

  const [tableParams, setTableParams] = useState({
    pagination: { current: 1, pageSize: 10 },
  });

  // ═══════════════════════════════════════════════════════
  // REFS
  // ═══════════════════════════════════════════════════════
  const navigate = useNavigate();
  const searchInput = useRef(null);
  const pinInputRef = useRef(null);
  const staffOutReasonInputRef = useRef(null);
  const inFlightRef = useRef(false);
  const abortRef = useRef(null);

  // ═══════════════════════════════════════════════════════
  // FETCH
  // ═══════════════════════════════════════════════════════
  const fetchData = useCallback(async (opts = { silent: false }) => {
    const silent = !!opts?.silent;
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try { abortRef.current?.abort?.(); } catch {}
    abortRef.current = new AbortController();

    try {
      if (silent) setIsRefreshing(true);
      else { setIsLoading(true); setError(null); }

      const res = await fetch(API_GETG, {
        method: "GET",
        headers: { Authorization: AUTH_KEY, "Content-Type": "application/json" },
        signal: abortRef.current.signal,
      });

      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

      const json = await res.json();
      const pickUserId = (x) =>
        x?.userid ?? x?.UserId ?? x?.userId ?? x?.Userid ?? x?.USERID ?? x?.id ?? x?.ID;

      setData(
        (json || []).map((x) => {
          const userid = pickUserId(x);
          return {
            ...x,
            userid: userid != null ? String(userid).trim() : "",
            key: userid ?? x.id ?? x.UserId ?? Math.random(),
          };
        })
      );
      setError(null);
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error(e);
      setError(e);
    } finally {
      inFlightRef.current = false;
      setIsRefreshing(false);
      setIsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => { fetchData({ silent: false }); }, [fetchData]);

  // Cleanup abort on unmount
  useEffect(() => {
    return () => { try { abortRef.current?.abort?.(); } catch {} };
  }, []);

  // Permissions
  useEffect(() => {
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    if (storedPermissions?.length > 0) {
      setPermissions(storedPermissions[0]);
      if (storedPermissions[0]?.Abone === 0) navigate("/");
    }
  }, [navigate]);

  const canEdit = permissions?.AboneEdit === 1;
  const canDelete = permissions?.AboneDelete === 1;
  const canBlock = permissions?.AboneBlock === 1;
  const canUnblock = permissions?.AboneUnblock === 1;
  const canExcel = permissions?.Admin === 1;

  // ═══════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════
  // berpa_count === 0 → qara siyahı; berpa_count > 0 → normal
  const isBlacklisted = (rec) =>
    rec?.BlackList === 1 || rec?.blacklist === 1 || rec?.Blacklist === 1 ||
    rec?.Status === "blacklist" || rec?.Status === "Blacklist" ||
    (rec?.berpa_count != null && Number(rec.berpa_count) > 0);

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const _toDate = (v) => {
    if (!v) return null;
    const s = String(v).trim();
    const d = new Date(s.includes(" ") ? s.replace(" ", "T") : s);
    return isNaN(d.getTime()) ? null : d;
  };

  const isPhoneVerified = (rec) => {
    const until = _toDate(rec?.phone_verified_until);
    if (until) return until.getTime() > Date.now();
    if (rec?.phone_verified != null) return Number(rec.phone_verified) === 1;
    return false;
  };

  const parseEndTime = (v) => {
    if (v == null || v === "" || v === 0) return null;
    if (typeof v === "number") {
      if (v > 1e12) return new Date(v);
      if (v > 1e9) return new Date(v * 1000);
      const s = String(v);
      if (s.length === 8) return new Date(`${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`);
      return null;
    }
    const s = String(v).trim();
    if (/^\d{2}\.\d{2}\.\d{4}/.test(s)) {
      const [datePart, timePart] = s.split(" ");
      const [dd, mm, yyyy] = datePart.split(".");
      const hh = timePart?.split(":")?.[0] ?? "00";
      const mi = timePart?.split(":")?.[1] ?? "00";
      const ss = timePart?.split(":")?.[2] ?? "00";
      return new Date(`${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}`);
    }
    const iso = s.includes(" ") ? s.replace(" ", "T") : s;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  };

  const diffDaysFromToday = (endDate) => {
    const today = startOfDay(new Date());
    const end = startOfDay(endDate);
    return Math.round((end.getTime() - today.getTime()) / MS_PER_DAY);
  };

  // ═══════════════════════════════════════════════════════
  // GLOBAL SEARCH FILTER
  // ═══════════════════════════════════════════════════════
  const filteredData = useMemo(() => {
    if (!globalSearch.trim()) return data;
    const q = globalSearch.toLowerCase().trim();
    return data.filter((r) =>
      [r.userid, r.username, r.surename, r.dadname, r.Phone, r.Fin]
        .some((v) => v && String(v).toLowerCase().includes(q))
    );
  }, [data, globalSearch]);

  // ═══════════════════════════════════════════════════════
  // SUMMARY
  // ═══════════════════════════════════════════════════════
  const summary = useMemo(() => {
    const rows = (data || []).map((rec) => {
      const d = parseEndTime(rec.endtime);
      const days = d ? diffDaysFromToday(d) : null;
      return {
        key: rec.key ?? rec.userid,
        userid: rec.userid,
        username: rec.username,
        surename: rec.surename,
        Phone: rec.Phone,
        endDate: d,
        endDateText: d ? d.toLocaleDateString("az-AZ") : "-",
        days,
        daysText: d ? (days >= 0 ? `Qalıb ${days} gün` : `Bitib ${Math.abs(days)} gün`) : "-",
      };
    });
    const hasDate = rows.filter((r) => r.endDate);
    const active = data.filter((r) => !r.Status && !isBlacklisted(r)).length;
    const blocked = data.filter((r) => r.Status && !isBlacklisted(r)).length;
    const blacklistedRows = data
      .filter((r) => isBlacklisted(r))
      .map((r) => ({
        key: r.key ?? r.userid,
        userid: r.userid,
        username: r.username,
        surename: r.surename,
        Phone: r.Phone,
        Fin: r.Fin,
        berpa_count: r.berpa_count,
      }));
    const blacklisted = blacklistedRows.length;
    const unverifiedPhone = data.filter((r) => !isPhoneVerified(r)).length;
    return {
      hasDate, active, blocked, blacklisted, blacklistedRows, unverifiedPhone,
      expired: hasDate.filter((r) => r.days < 0),
      todayExp: hasDate.filter((r) => r.days === 0),
      soon7: hasDate.filter((r) => r.days > 0 && r.days <= 7),
      soon30: hasDate.filter((r) => r.days > 7 && r.days <= 30),
    };
  }, [data]);

  // ═══════════════════════════════════════════════════════
  // PHONE VERIFY
  // ═══════════════════════════════════════════════════════
  const verifyPhone = async (userid) => {
    setPhoneVerifyLoading((p) => ({ ...p, [userid]: true }));
    try {
      const fd = new FormData();
      fd.append("userid", String(userid));
      const res = await axios.post(API_VERIFY_PHONE, fd);
      if (res?.data?.status === 1) {
        toast.success(res?.data?.message || "Telefon təsdiqləndi.");
        addAuditLog("Telefon təsdiq", userid);
        await fetchData({ silent: true });
      } else {
        toast.error(res?.data?.message || "Telefon təsdiqlənmədi.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Serverlə əlaqə zamanı xəta baş verdi.");
    } finally {
      setPhoneVerifyLoading((p) => ({ ...p, [userid]: false }));
    }
  };

  // ═══════════════════════════════════════════════════════
  // CRUD ACTIONS
  // ═══════════════════════════════════════════════════════
  const showDeleteConfirm = (record) => {
    setItemToDelete(record);
    setDeleteOpen(true);
    setTimeout(() => pinInputRef.current?.focus(), 50);
  };

  const handleDelete = async () => {
    try {
      if (pin !== "0001") {
        toast.error("Yanlış PIN. Element silinməyib.");
        return;
      }
      const response = await axios.delete(`${CONN_KEY_SENAT}AddGrils.php?userid=${itemToDelete.userid}`);
      if (response.status === 200 && response.data.code === 200) {
        toast.success("Abunəçi uğurla silindi.");
        addAuditLog("Silindi", itemToDelete.userid);
        await fetchData({ silent: true });
      } else {
        toast.error("API-dən xəta mesajı gəldi.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Abunəçini silərkən xəta baş verdi.");
    } finally {
      setDeleteOpen(false);
      setItemToDelete(null);
      setPin("");
    }
  };

  const handleBlock = (record) => {
    setSelectedRecord(record);
    setStaffOutReason("");
    setStaffOutDate("");
    setBlockOpen(true);
    setTimeout(() => staffOutReasonInputRef.current?.focus(), 50);
  };

  const submitBlock = async () => {
    try {
      await axios.post(`${CONN_KEY_SENAT}BlockSebGirls.php`, {
        userid: selectedRecord.userid,
        BlockSeb: staffOutReason,
        BlockSebdate: staffOutDate,
      });
      toast.success("Abonent bloklandı.");
      addAuditLog("Bloklandı", selectedRecord.userid, staffOutReason);
      setBlockOpen(false);
      await fetchData({ silent: true });
    } catch (e) {
      console.error(e);
      toast.error("Blok edərkən xəta baş verdi.");
    }
  };

  const handleUnBlock = (record) => {
    Modal.confirm({
      title: "Diqqət",
      content: "Bu Abonəçinin Blokunu qaldırmaq istədiyinizə əminsiniz?",
      okText: "Bloku qaldır",
      okType: "danger",
      cancelText: "Ləğv et",
      onOk: async () => {
        try {
          const formData = new FormData();
          formData.append("userid", record.userid);
          await axios.post(`${CONN_KEY_SENAT}UnblockGirls.php`, formData);
          toast.success("Blok qaldırıldı.");
          addAuditLog("Blok qaldırıldı", record.userid);
          await fetchData({ silent: true });
        } catch (e) {
          console.error(e);
          toast.error("Unblock zamanı xəta baş verdi.");
        }
      },
    });
  };

  const showTimeModal = (record) => {
    setSelectedRecord(record);
    setCardId("");
    setTimeOpen(true);
  };

  const handleCardClick = async (duration) => {
    if (!cardId) {
      toast.warning("Zəhmət olmasa, Kart ID-ni daxil edin.");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("userid", selectedRecord.userid);
      formData.append("cardId", cardId);
      formData.append("duration", duration);
      const response = await axios.post(`${CONN_KEY_SENAT}timeGitls.php`, formData);
      if (response.data.status === 1) {
        toast.success(response.data.message);
        addAuditLog(`Müddət +${duration} ay`, selectedRecord.userid, `Kart: ${cardId}`);
        setTimeOpen(false);
        setCardId("");
        navigate(`/CartUzv/${selectedRecord.userid}`);
        await fetchData({ silent: true });
      } else {
        toast.error(response.data.message || "Müddət artırılarkən xəta baş verdi.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Serverlə əlaqə zamanı xəta baş verdi.");
    }
  };

  // ═══════════════════════════════════════════════════════
  // BULK ACTIONS
  // ═══════════════════════════════════════════════════════
  const handleBulkBlock = async () => {
    if (!bulkBlockReason.trim()) {
      toast.warning("Blok səbəbini daxil edin.");
      return;
    }
    let successCount = 0;
    for (const key of selectedRowKeys) {
      try {
        const rec = data.find((r) => String(r.key) === String(key) || String(r.userid) === String(key));
        if (!rec) continue;
        await axios.post(`${CONN_KEY_SENAT}BlockSebGirls.php`, {
          userid: rec.userid,
          BlockSeb: bulkBlockReason,
          BlockSebdate: "",
        });
        addAuditLog("Toplu blok", rec.userid, bulkBlockReason);
        successCount++;
      } catch {}
    }
    toast.success(`${successCount} abonent bloklandı.`);
    setBulkBlockOpen(false);
    setBulkBlockReason("");
    setSelectedRowKeys([]);
    await fetchData({ silent: true });
  };

  const handleBulkUnblock = () => {
    Modal.confirm({
      title: "Toplu Blok Qaldır",
      content: `${selectedRowKeys.length} abonentin bloku qaldırılsın?`,
      okText: "Qaldır",
      okType: "danger",
      cancelText: "Ləğv et",
      onOk: async () => {
        let successCount = 0;
        for (const key of selectedRowKeys) {
          try {
            const rec = data.find((r) => String(r.key) === String(key) || String(r.userid) === String(key));
            if (!rec) continue;
            const formData = new FormData();
            formData.append("userid", rec.userid);
            await axios.post(`${CONN_KEY_SENAT}UnblockGirls.php`, formData);
            addAuditLog("Toplu blok qaldırıldı", rec.userid);
            successCount++;
          } catch {}
        }
        toast.success(`${successCount} abonentin bloku qaldırıldı.`);
        setSelectedRowKeys([]);
        await fetchData({ silent: true });
      },
    });
  };

  // ═══════════════════════════════════════════════════════
  // EXCEL EXPORT
  // ═══════════════════════════════════════════════════════
  const handleExport = () => {
    const exportData = (selectedRowKeys.length > 0
      ? filteredData.filter((r) => selectedRowKeys.includes(r.key) || selectedRowKeys.includes(r.userid))
      : filteredData
    ).map((r) => ({
      "V/N": r.userid,
      "Ad": r.username,
      "Soyad": r.surename,
      "Ata adı": r.dadname,
      "Telefon": r.Phone,
      "FİN": r.Fin,
      "Status": isBlacklisted(r) ? "Qara siyahı" : r.Status ? "Blok" : "Aktiv",
      "Telefon təsdiqlənib": isPhoneVerified(r) ? "Bəli" : "Xeyr",
      "Bitmə vaxtı": (() => {
        const d = parseEndTime(r.endtime);
        return d ? d.toLocaleDateString("az-AZ") : "-";
      })(),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Abonentlər");
    XLSX.writeFile(wb, `abonentler_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Excel faylı yükləndi.");
    addAuditLog("Excel export", "-", `${exportData.length} sətir`);
  };

  // ═══════════════════════════════════════════════════════
  // COLUMN VISIBILITY
  // ═══════════════════════════════════════════════════════
  const ALL_COL_KEYS = ["filePP", "userid", "username", "surename", "dadname", "Phone", "Fin", "Status", "endtime", "action", "more"];
  const COL_LABELS = {
    filePP: "3x4", userid: "V/N", username: "İstifadəçi adı", surename: "Soyad",
    dadname: "Ata adı", Phone: "Telefon", Fin: "FİN", Status: "Status",
    endtime: "Bitmə vaxtı", action: "Action", more: "More",
  };

  const toggleCol = (key) => {
    const updated = hiddenCols.includes(key)
      ? hiddenCols.filter((k) => k !== key)
      : [...hiddenCols, key];
    setHiddenCols(updated);
    localStorage.setItem("appgirls_hidden_cols", JSON.stringify(updated));
  };

  // ═══════════════════════════════════════════════════════
  // TABLE SEARCH
  // ═══════════════════════════════════════════════════════
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0] || "");
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(String(value).toLowerCase())
        : "",
    filterDropdownProps: {
      onOpenChange: (open) => {
        if (open) setTimeout(() => searchInput.current?.select(), 100);
      },
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (text),
  });

  // ═══════════════════════════════════════════════════════
  // MORE DROPDOWN
  // ═══════════════════════════════════════════════════════
  const renderActions = (value, record) => {
    const userid = String(value ?? record?.userid ?? record?.id ?? "").trim();
    const verified = isPhoneVerified(record);

    const items = [
      !verified && {
        key: "verify-phone",
        label: "Telefonu təsdiq et",
        icon: <SafetyOutlined />,
        disabled: !!phoneVerifyLoading[userid],
      },
      canDelete && { key: "delete", label: "Sil", icon: <DeleteOutlined />, danger: true },
      canEdit && { key: "edit", label: "Redaktə et", icon: <EditOutlined /> },
      canBlock && { key: "block", label: "Blokla", icon: <StopOutlined /> },
      canUnblock && { key: "unblock", label: "Bloku qaldır", icon: <StopOutlined /> },
      { key: "divider", type: "divider" },
      { key: "pdf", label: "PDF bax", icon: <FilePdfOutlined /> },
    ].filter(Boolean);

    const onClick = ({ key }) => {
      if (!userid) { toast.error("Bu sətirdə userid tapılmadı."); return; }
      if (key === "verify-phone") return verifyPhone(userid);
      if (key === "delete") return showDeleteConfirm(record);
      if (key === "edit") return navigate(`/Update-Üzv/${userid}`);
      if (key === "block") return handleBlock(record);
      if (key === "unblock") return handleUnBlock(record);
      if (key === "pdf") return window.open(`/PDFUzv/${record.userid}`, "_blank");
    };

    return (
      <Dropdown menu={{ items, onClick }} placement="bottomRight" trigger={["click"]}>
        <Button icon={<MoreOutlined />} loading={!!phoneVerifyLoading[userid]} size="small" />
      </Dropdown>
    );
  };

  // ═══════════════════════════════════════════════════════
  // TABLE COLUMNS
  // ═══════════════════════════════════════════════════════
  const allColumns = [
    {
      title: "3x4", dataIndex: "filePP", key: "filePP", width: 80,
      render: (imagePath, record) => {
        const blacklisted = isBlacklisted(record);
        const blocked = !!record.Status && !blacklisted;
        return (
          <div style={{ display: "flex", justifyContent: "center", position: "relative" }}>
            {(blacklisted || blocked) && (
              <StopOutlined style={{
                pointerEvents: "none", color: blacklisted ? "#722ed1" : "red",
                fontSize: 20, position: "absolute", top: 6, right: 10, zIndex: 2,
              }} />
            )}
            <Watermark content="Senate">
              <Image
                className={`imgpp ${(blacklisted || blocked) ? "grayscale" : ""}`}
                src={`${CONN_KEY_SENAT}3x4Girl/${imagePath}`}
                alt="filePP" width={56} height={74}
                style={{ objectFit: "cover", borderRadius: 6, border: "1px solid #f0f0f0" }}
              />
            </Watermark>
          </div>
        );
      },
    },
    {
      title: "V/N", dataIndex: "userid", key: "userid", width: 90,
      sorter: (a, b) => Number(a.userid || 0) - Number(b.userid || 0),
      render: (v) => <span style={{ fontWeight: 700 }}>{v}</span>,
      ...getColumnSearchProps("userid"),
    },
    { title: "İstifadəçi adı", dataIndex: "username", width: 120, key: "username", ...getColumnSearchProps("username") },
    { title: "Soyad", dataIndex: "surename", width: 120, key: "surename", ...getColumnSearchProps("surename") },
    { title: "Ata adı", dataIndex: "dadname", width: 110, key: "dadname", ...getColumnSearchProps("dadname") },
    {
      title: "Telefon", dataIndex: "Phone", width: 150, key: "Phone",
      ...getColumnSearchProps("Phone"),
      render: (phone, record) => {
        const ok = isPhoneVerified(record);
        return (
          <Tooltip title={ok ? "Telefon təsdiqlənib" : "Telefon təsdiqlənməyib"}>
            <Tag
              color={ok ? "green" : "red"}
              icon={ok ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              style={{ margin: 0, cursor: "default" }}
            >
              {phone || "-"}
            </Tag>
          </Tooltip>
        );
      },
    },
    { title: "FİN", dataIndex: "Fin", width: 100, key: "Fin", ...getColumnSearchProps("Fin") },
    {
      title: "Status", dataIndex: "Status", key: "Status", width: 110,
      filters: [
        { text: "Aktiv", value: "active" },
        { text: "Blok", value: "blocked" },
        { text: "Qara siyahı", value: "blacklist" },
      ],
      onFilter: (value, record) => {
        if (value === "active") return !record.Status && !isBlacklisted(record);
        if (value === "blocked") return !!record.Status && !isBlacklisted(record);
        if (value === "blacklist") return isBlacklisted(record);
        return true;
      },
      render: (_, record) => {
        if (isBlacklisted(record)) return <Tag color="magenta" icon={<StopOutlined />}>Qara siyahı</Tag>;
        return <Tag color={record.Status ? "red" : "green"} icon={record.Status ? <StopOutlined /> : <CheckCircleOutlined />}>{record.Status ? "Blok" : "Aktiv"}</Tag>;
      },
    },
    {
      title: "Bitmə vaxtı", dataIndex: "endtime", key: "endtime", width: 155,
      sorter: (a, b) => {
        const da = parseEndTime(a.endtime);
        const db = parseEndTime(b.endtime);
        return (da?.getTime() || 0) - (db?.getTime() || 0);
      },
      render: (v) => {
        const d = parseEndTime(v);
        if (!d) return <Tag>-</Tag>;
        const days = diffDaysFromToday(d);
        const text = days >= 0 ? `Qalıb ${days} gün` : `Bitib ${Math.abs(days)} gün`;
        const color = days < 0 ? "red" : days <= 7 ? "orange" : "green";
        const icon = days < 0 ? <CloseCircleOutlined /> : days <= 7 ? <WarningOutlined /> : <CheckCircleOutlined />;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Tag color={color} icon={icon} style={{ marginRight: 0 }}>{text}</Tag>
            <span style={{ fontSize: 11, color: "#888" }}>{d.toLocaleDateString("az-AZ")}</span>
          </div>
        );
      },
    },
    {
      title: "Action", key: "action", width: 110,
      render: (_, record) => {
        const ok = isPhoneVerified(record);
        const userid = String(record?.userid ?? "").trim();
        return (
          <Space size={4}>
            <Tooltip title={ok ? "Telefon təsdiqlənib" : "Telefonu təsdiq et"}>
              <Button
                type={ok ? "primary" : "default"}
                style={ok ? { background: "#16a34a", borderColor: "#16a34a" } : undefined}
                disabled={ok || !userid}
                loading={!!phoneVerifyLoading[userid]}
                icon={<SafetyOutlined />}
                size="small"
                onClick={() => verifyPhone(userid)}
              />
            </Tooltip>
            <Tooltip title="Müddət artır">
              <Button className="ColorMain" onClick={() => showTimeModal(record)} icon={<IdcardOutlined />} size="small" />
            </Tooltip>
            <Tooltip title="PDF bax">
              <Link to={`/PDFUzv/${record.userid}`} target="_blank">
                <Button className="ColorMain" icon={<FilePdfOutlined />} size="small" />
              </Link>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: "More", dataIndex: "userid", key: "more", fixed: "right",
      render: renderActions, width: 60,
    },
  ];

  const columns = allColumns.filter((c) => !hiddenCols.includes(c.key));

  // ═══════════════════════════════════════════════════════
  // ROW CLASS (rəng kodlaması)
  // ═══════════════════════════════════════════════════════
  const rowClassName = (record) => {
    if (isBlacklisted(record)) return "row-blacklist";
    if (record.Status) return "row-blocked";
    const d = parseEndTime(record.endtime);
    if (d) {
      const days = diffDaysFromToday(d);
      if (days < 0) return "row-expired";
      if (days === 0) return "row-today";
      if (days <= 7) return "row-soon7";
    }
    return "";
  };

  const handleTableChange = (pagination) => setTableParams((p) => ({ ...p, pagination }));

  if (isLoading) return <Skeleton active />;

  // ═══════════════════════════════════════════════════════
  // COLUMN TOGGLE POPOVER CONTENT
  // ═══════════════════════════════════════════════════════
  const colToggleContent = (
    <div style={{ minWidth: 180 }}>
      {ALL_COL_KEYS.map((key) => (
        <div key={key} style={{ padding: "4px 0", display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
          onClick={() => toggleCol(key)}>
          <Checkbox checked={!hiddenCols.includes(key)} onChange={() => toggleCol(key)} />
          <span style={{ fontSize: 13 }}>{COL_LABELS[key]}</span>
        </div>
      ))}
    </div>
  );

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════
  return (
    <>
      {/* ── Inline row color styles ───────────────────── */}
      <style>{`
        .row-expired td { background: #fff1f0 !important; }
        .row-today td   { background: #fffbe6 !important; }
        .row-soon7 td   { background: #fff7e6 !important; }
        .row-expired:hover td { background: #ffd6d6 !important; }
        .row-today:hover td   { background: #fff1b8 !important; }
        .row-soon7:hover td   { background: #ffd591 !important; }
      `}</style>

      {data.length > 0 ? (
        <>
          {/* ── DASHBOARD STATS ────────────────────────── */}
          <div style={{ marginBottom: 16 }}>
            <Row gutter={[12, 12]}>
              <Col xs={12} sm={6}>
                <Card size="small" style={{ borderLeft: "3px solid #52c41a" }}>
                  <Statistic title="Aktiv" value={summary.active}
                    prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                    valueStyle={{ color: "#52c41a", fontSize: 22 }} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" style={{ borderLeft: "3px solid #ff4d4f" }}>
                  <Statistic title="Bloklu" value={summary.blocked}
                    prefix={<StopOutlined style={{ color: "#ff4d4f" }} />}
                    valueStyle={{ color: "#ff4d4f", fontSize: 22 }} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card
                  size="small"
                  hoverable
                  style={{ borderLeft: "3px solid #722ed1", cursor: "pointer" }}
                  onClick={() => {
                    setSummaryTitle("Qara siyahı abonentlər");
                    setSummaryRows(summary.blacklistedRows);
                    setSummaryOpen(true);
                  }}
                >
                  <Statistic title="Qara siyahı" value={summary.blacklisted}
                    prefix={<StopOutlined style={{ color: "#722ed1" }} />}
                    valueStyle={{ color: "#722ed1", fontSize: 22 }} />
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" style={{ borderLeft: "3px solid #fa8c16" }}>
                  <Statistic title="Telefon təsdiqlənməyib" value={summary.unverifiedPhone}
                    prefix={<ClockCircleOutlined style={{ color: "#fa8c16" }} />}
                    valueStyle={{ color: "#fa8c16", fontSize: 22 }} />
                </Card>
              </Col>
            </Row>
          </div>

          {/* ── SUMMARY EXPIRY CARDS ────────────────────── */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <Text strong>Bugün: {new Date().toLocaleDateString("az-AZ")}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Endtime olanlar: {summary.hasDate.length} / {data.length}
              </Text>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {[
                { title: "Bu gün bitən", rows: summary.todayExp, color: "#fa8c16" },
                { title: "Bitmiş", rows: summary.expired, color: "#ff4d4f" },
                { title: "7 gün içində", rows: summary.soon7, color: "#faad14" },
                { title: "8–30 gün içində", rows: summary.soon30, color: "#52c41a" },
              ].map(({ title, rows, color }) => (
                <Card
                  key={title}
                  hoverable
                  size="small"
                  style={{ minWidth: 160, borderTop: `3px solid ${color}`, cursor: "pointer" }}
                  onClick={() => { setSummaryTitle(title); setSummaryRows(rows); setSummaryOpen(true); }}
                >
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color }}>{rows.length}</div>
                </Card>
              ))}
            </div>
          </div>

          {/* ── TOOLBAR ─────────────────────────────────── */}
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
            {/* Global search */}
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#bbb" }} />}
              placeholder="Ad, soyad, telefon, FİN axtar..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              style={{ width: 260 }}
            />

            {/* Column toggle */}
            <Popover content={colToggleContent} title="Sütunlar" trigger="click" placement="bottomLeft">
              <Button icon={<EyeOutlined />}>Sütunlar</Button>
            </Popover>

            {/* Excel export — yalnız adminlər görə bilər */}
            {canExcel && (
              <Button icon={<DownloadOutlined />} onClick={handleExport} type="default">
                Excel {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length} seçili)` : ""}
              </Button>
            )}

            {/* Audit log */}
            <Button icon={<ClockCircleOutlined />} onClick={() => setAuditOpen(true)}>
              Tarixçə
            </Button>

            {/* Bulk actions (only when rows selected) */}
            {selectedRowKeys.length > 0 && (
              <Space>
                <Text type="secondary" style={{ fontSize: 12 }}>{selectedRowKeys.length} seçili</Text>
                {canBlock && (
                  <Button size="small" danger icon={<StopOutlined />} onClick={() => setBulkBlockOpen(true)}>
                    Toplu blok
                  </Button>
                )}
                {canUnblock && (
                  <Button size="small" icon={<StopOutlined />} onClick={handleBulkUnblock}>
                    Toplu blok qaldır
                  </Button>
                )}
                <Button size="small" onClick={() => setSelectedRowKeys([])}>Seçimi ləğv et</Button>
              </Space>
            )}
          </div>

          {/* ── MAIN TABLE ──────────────────────────────── */}
          <Table
            columns={columns}
            sticky
            dataSource={filteredData}
            onChange={handleTableChange}
            scroll={{ y: 520, x: "max-content" }}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              preserveSelectedRowKeys: true,
            }}
            pagination={{
              ...tableParams.pagination,
              showSizeChanger: true,
              showTotal: (total) => `Cəmi ${total} abonent`,
              pageSizeOptions: ["5", "10", "20", "30", "50", "100", "200", "500"],
            }}
            locale={{
              emptyText: <Empty description="Məlumat yoxdur" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 50 }} />,
            }}
            rowClassName={rowClassName}
          />

          {/* ── SUMMARY MODAL ───────────────────────────── */}
          <Modal
            title={
              <span>
                {summaryTitle === "Qara siyahı abonentlər" && (
                  <Tag color="magenta" style={{ marginRight: 8 }}>
                    <StopOutlined /> {summary.blacklisted} nəfər
                  </Tag>
                )}
                {summaryTitle}
              </span>
            }
            open={summaryOpen}
            onCancel={() => setSummaryOpen(false)}
            footer={null} width={920} destroyOnClose
          >
            {summaryRows.length === 0 ? (
              <Empty description="Heç kim yoxdur" />
            ) : (
              <Table
                size="small"
                dataSource={summaryRows}
                rowKey="key"
                pagination={{ pageSize: 15, showSizeChanger: true }}
                columns={
                  summaryTitle === "Qara siyahı abonentlər"
                    ? [
                        { title: "V/N", dataIndex: "userid", key: "userid", width: 80,
                          sorter: (a, b) => Number(a.userid) - Number(b.userid) },
                        { title: "Ad", dataIndex: "username", key: "username", width: 120 },
                        { title: "Soyad", dataIndex: "surename", key: "surename", width: 120 },
                        { title: "Telefon", dataIndex: "Phone", key: "Phone", width: 140,
                          render: (v) => <Tag color="red">{v || "-"}</Tag> },
                        { title: "FİN", dataIndex: "Fin", key: "Fin", width: 100 },
                        {
                          title: "Bərpa sayı", dataIndex: "berpa_count", key: "berpa_count", width: 110,
                          sorter: (a, b) => Number(a.berpa_count ?? 0) - Number(b.berpa_count ?? 0),
                          render: (v) => (
                            <Tag color={Number(v) === 0 ? "magenta" : "orange"}>
                              {v ?? "-"}
                            </Tag>
                          ),
                        },
                        {
                          title: "Profil", key: "profile", width: 80,
                          render: (_, r) => (
                            <Link to={`/PDFUzv/${r.userid}`} target="_blank">
                              <Button size="small" icon={<FilePdfOutlined />} />
                            </Link>
                          ),
                        },
                      ]
                    : [
                        { title: "V/N", dataIndex: "userid", key: "userid", width: 80 },
                        { title: "Ad Soyad", key: "full", render: (_, r) => `${r.username || ""} ${r.surename || ""}`.trim() || "-" },
                        { title: "Telefon", dataIndex: "Phone", key: "Phone", width: 140 },
                        { title: "Bitmə tarixi", dataIndex: "endDateText", key: "endDateText", width: 130 },
                        {
                          title: "Gün", dataIndex: "daysText", key: "daysText",
                          render: (t, r) => {
                            const days = r.days ?? 0;
                            const color = days < 0 ? "red" : days === 0 ? "gold" : days <= 7 ? "orange" : "green";
                            return <Tag color={color}>{t}</Tag>;
                          },
                        },
                      ]
                }
              />
            )}
          </Modal>

          {/* ── TIME MODAL ──────────────────────────────── */}
          <Modal
            title={`Müddət artır — ${selectedRecord?.username || ""} ${selectedRecord?.surename || ""}`}
            open={timeOpen}
            onCancel={() => { setTimeOpen(false); setCardId(""); }}
            footer={null}
            destroyOnClose
          >
            <Input
              placeholder="Kart ID-ni daxil edin"
              type="password"
              value={cardId}
              onChange={(e) => setCardId(e.target.value)}
              style={{ marginBottom: 20 }}
            />
            <div className="flex gap-2">
              <Card hoverable onClick={() => handleCardClick(3)} style={{ flex: 1 }}>
                <img src={img3} alt="3 Ay" style={{ width: "100%" }} />
              </Card>
              <Card hoverable onClick={() => handleCardClick(3)} style={{ flex: 1 }}>
                <img src={img3} alt="3 Ay" style={{ width: "100%" }} />
              </Card>
            </div>
          </Modal>

          {/* ── BLOCK MODAL ─────────────────────────────── */}
          <Modal title="Blok səbəbi" open={blockOpen} onOk={submitBlock}
            onCancel={() => setBlockOpen(false)} okText="Blokla" cancelText="Ləğv et" destroyOnClose>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Input
                ref={staffOutReasonInputRef}
                placeholder="Blok səbəbi"
                value={staffOutReason}
                onChange={(e) => setStaffOutReason(e.target.value)}
              />
              <Input
                placeholder="Tarix (isteğe bağlı)"
                value={staffOutDate}
                onChange={(e) => setStaffOutDate(e.target.value)}
              />
            </Space>
          </Modal>

          {/* ── DELETE MODAL ─────────────────────────────── */}
          <Modal title="Silməyi Təsdiqlə" open={deleteOpen} onOk={handleDelete}
            onCancel={() => setDeleteOpen(false)} okText="Sil" okType="danger" cancelText="Ləğv et">
            <p style={{ marginBottom: 12 }}>
              Bu abunəçini silmək istədiyinizə əminsiniz? Bu əməliyyat geri qaytarıla bilməz.
              Silməni təsdiqləmək üçün PIN daxil edin:
            </p>
            <Input ref={pinInputRef} type="password" value={pin} onChange={(e) => setPin(e.target.value)} />
          </Modal>

          {/* ── BULK BLOCK MODAL ────────────────────────── */}
          <Modal title={`Toplu Blok — ${selectedRowKeys.length} abonent`}
            open={bulkBlockOpen} onOk={handleBulkBlock}
            onCancel={() => setBulkBlockOpen(false)} okText="Blokla" okType="danger" cancelText="Ləğv et">
            <Input
              placeholder="Blok səbəbini daxil edin"
              value={bulkBlockReason}
              onChange={(e) => setBulkBlockReason(e.target.value)}
            />
          </Modal>

          {/* ── AUDIT LOG MODAL ─────────────────────────── */}
          <Modal title="Əməliyyat Tarixçəsi" open={auditOpen}
            onCancel={() => setAuditOpen(false)} footer={null} width={800} destroyOnClose>
            <Table
              size="small"
              dataSource={getAuditLog()}
              rowKey="id"
              pagination={{ pageSize: 15, showSizeChanger: true }}
              columns={[
                { title: "Vaxt", dataIndex: "time", key: "time", width: 160 },
                { title: "Əməliyyat", dataIndex: "action", key: "action", width: 150,
                  render: (v) => <Tag color="blue">{v}</Tag> },
                { title: "User ID", dataIndex: "userid", key: "userid", width: 100 },
                { title: "Əlavə məlumat", dataIndex: "detail", key: "detail" },
              ]}
              locale={{ emptyText: <Empty description="Hələ heç bir əməliyyat yoxdur" /> }}
            />
          </Modal>
        </>
      ) : (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Empty description="Məlumat tapılmadı" />
          <Button type="primary" className="ColorMain" style={{ marginTop: 16 }}>
            <Link to="/add">Əlavə et</Link>
          </Button>
        </div>
      )}

      {error && (
        <div style={{ marginTop: 12, color: "crimson", fontSize: 12 }}>
          Xəta: {String(error?.message || error)}
        </div>
      )}
    </>
  );
}

export default AppGirls;